<template>
  <fragment v-if="campaign">
    <div class="alert"
         :class="{ 'alert-success': campaignStatusType === CampaignStatusType.Active, 'alert-danger': campaignStatusType === CampaignStatusType.NotActive }">
      <icon-base width="18" height="18" view-box="0 0 18 18" icon-name="check-circle"
                 v-if="campaignStatusType === CampaignStatusType.Active">
        <icon-check-circle/>
      </icon-base>
      {{ statusString }}
    </div>
  </fragment>
</template>

<script lang="ts">
import {Component, Vue, Watch} from "vue-property-decorator";
import VueCookies from "vue-cookies";
import Fragment from "vue-fragment";
import IconBase from "../IconBase.vue";
import IconCaretDown from "../icons/IconCaretDown.vue";
import IconCheckCircle from "../icons/IconCheckCircle.vue";
import {Campaign, CampaignEditViewModel} from "@/models/campaigns/Campaign";
import {Getter} from "vuex-class";
import {ChannelType} from "@/models/campaigns/ChannelType";
import i18n from "@/i18n";

Vue.use(Fragment.Plugin);
Vue.use(VueCookies);
const namespace = "campaignsModule";

@Component({
  components: {
    IconBase,
    IconCaretDown,
    IconCheckCircle,
  },
})
export default class CampaignStatus extends Vue {

  //#region Vuex getters and actions

  @Getter("campaignEditViewModel", {namespace}) campaignEditViewModel!: CampaignEditViewModel;

  //#endregion Vuex getters and actions


  //#region Properties

  statusString = "";
  campaignStatusType = CampaignStatusType.NotActive;

  CampaignStatusType = CampaignStatusType;
  ChannelMetadataType = ChannelType;

  //#endregion Properties


  //#region Watchers

  @Watch("campaignEditViewModel", {immediate: true})
  onCampaignEditViewModelChange(newCampaignEditViewModel: CampaignEditViewModel, oldCampaignEditViewModel: CampaignEditViewModel): void {
    if (!newCampaignEditViewModel) {
      return;
    }
    this.getCampaignStatus();
  }

  //#endregion Watchers


  //#region Getters

  get campaign(): Campaign {
    return this.campaignEditViewModel?.Campaign;
  }

  //#endregion Getters


  //#region Private methods

  getCampaignStatus(): void {
    if (!this.campaign.IsMediaChannel && this.campaign.ChannelType !== ChannelType.Phone) {
      if (this.campaignEditViewModel.IsGoogle && !this.campaignEditViewModel.GoogleValid || this.campaignEditViewModel.IsFacebook && !this.campaignEditViewModel.FacebookValid) {
        this.statusString = i18n.t("CampaignEdit.CampaignStatus.ConnectionRequired").toString();
      } else {
        if (this.campaign.IsGoogleFacebookChannel) {
          if (this.campaign.ExternalTokenInvalid) {
            if (this.campaign.ChannelType === ChannelType.GoogleAdwords) {
              this.statusString = i18n.t("CampaignEdit.CampaignStatus.GoogleTokenInvalid").toString();
            } else {
              this.statusString = i18n.t("CampaignEdit.CampaignStatus.FacebookTokenInvalid").toString();
            }
          } else {
            if (this.campaign.TokenValid) {
              if (this.campaign.Id > 0) {
                this.statusString = i18n.t("CampaignEdit.CampaignStatus.StatusInfo").toString() + " ";
              }
              if (this.campaign.Active) {
                this.statusString += i18n.t("CampaignEdit.CampaignStatus.Active").toString();
                this.campaignStatusType = CampaignStatusType.Active;
              } else {
                this.statusString += i18n.t("CampaignEdit.CampaignStatus.NotActive").toString();
              }
            } else {
              if (this.campaign.ChannelType === ChannelType.GoogleAdwords) {
                this.statusString = i18n.t("CampaignEdit.CampaignStatus.GoogleInvalidPermissions").toString();
              } else {
                this.statusString = i18n.t("CampaignEdit.CampaignStatus.FacebookInvalidPermissions").toString();
              }
            }
          }
        } else {
          if (this.campaign.Id > 0) {
            this.statusString = i18n.t("CampaignEdit.CampaignStatus.StatusInfo").toString() + " ";
          }
          if (this.campaign.Active) {
            this.statusString += i18n.t("CampaignEdit.CampaignStatus.Active").toString();
            this.campaignStatusType = CampaignStatusType.Active;
          } else {
            this.statusString += i18n.t("CampaignEdit.CampaignStatus.NotActive").toString();
          }
        }
      }
    } else {
      if (this.campaign.PhoneId || this.campaign.WhatsAppPhoneId) {
        if (this.campaign.Id > 0) {
          this.statusString = i18n.t("CampaignEdit.CampaignStatus.StatusInfo").toString() + " ";
        }
        if (this.campaign.Active) {
          this.statusString += i18n.t("CampaignEdit.CampaignStatus.Active").toString();
          this.campaignStatusType = CampaignStatusType.Active;
        } else {
          this.statusString += i18n.t("CampaignEdit.CampaignStatus.NotActive").toString();
        }
      } else {
        this.statusString = i18n.t("CampaignEdit.CampaignStatus.PhoneIsRequired").toString();
      }
    }
  }

  //#endregion Private methods
}

enum CampaignStatusType {
  Active,
  NotActive
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.alert {
  margin-bottom: 1rem;

  &.alert-danger, &.alert-success {
    align-items: center;
    color: $success;
    display: flex;
    font-size: 1rem;
    font-weight: 600;
    line-height: 22px;

    .icon-w {
      margin-right: 8px;
    }
  }

  &.alert-danger {
    color: $danger;
  }

  &.alert-success {
    color: $success;
  }
}

html[dir="rtl"] {
  .alert {
    &.alert-danger, &.alert-success {
      .icon-w {
        margin-left: 8px;
        margin-right: 0;
      }
    }
  }
}

</style>
